import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { PHONE, PHONE_TEL, EMAIL, LOCATION } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
  { icon: Phone, label: "Telefon", value: PHONE, href: `tel:${PHONE_TEL}` },
  { icon: Mail, label: "E-Mail", value: EMAIL, href: `mailto:${EMAIL}` },
  { icon: MapPin, label: "Standort", value: LOCATION, href: "#map" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Vielen Dank! Ihre Anfrage wurde erfolgreich gesendet. Wir melden uns innerhalb von 24 Stunden bei Ihnen.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Etwas ist schiefgelaufen. Bitte rufen Sie uns direkt an.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3.5 text-white placeholder:text-white/35 outline-none focus:border-volt focus:ring-1 focus:ring-volt transition-all";

  return (
    <section id="kontakt" className="relative py-24 md:py-32 bg-ink-surface" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center gap-3 mb-5">
          <span className="h-px w-10 bg-volt" />
          <span className="text-xs md:text-sm font-bold tracking-[0.25em] uppercase text-volt">Kontakt</span>
        </div>
        <h2 className="font-heading font-black uppercase tracking-tighter text-4xl md:text-6xl text-white leading-[0.95] mb-14">
          Jetzt <span className="text-volt">anfragen</span>
        </h2>

        <div className="grid lg:grid-cols-2 gap-8">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              {INFO.map((it) => (
                <a key={it.label} href={it.href} data-testid={`contact-${it.label}`}
                  className="group flex items-center gap-5 rounded-2xl bg-ink border border-white/[0.06] p-6 hover:border-volt transition-all duration-400">
                  <span className="grid place-items-center h-12 w-12 rounded-full bg-volt/10 text-volt group-hover:bg-volt group-hover:text-black transition-all">
                    <it.icon className="h-5 w-5" strokeWidth={2} />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-white/40 font-semibold">{it.label}</div>
                    <div className="text-white font-bold mt-0.5">{it.value}</div>
                  </div>
                </a>
              ))}
            </div>
            <div id="map" className="rounded-2xl overflow-hidden border border-white/[0.06] grow min-h-[240px]">
              <iframe title="VoltCore Standort" data-testid="google-map" className="w-full h-full min-h-[240px] grayscale contrast-125"
                src="https://www.google.com/maps?q=Heuchlingerstr.+10,+91224+Pommelsbrunn+Deutschland&output=embed" loading="lazy" />
            </div>
          </motion.div>

          <motion.form onSubmit={submit} data-testid="contact-form"
            initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="rounded-2xl bg-ink border border-white/[0.06] p-8 md:p-10 flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <input required value={form.name} onChange={set("name")} placeholder="Name *" className={inputCls} data-testid="input-name" />
              <input required type="email" value={form.email} onChange={set("email")} placeholder="E-Mail *" className={inputCls} data-testid="input-email" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input value={form.phone} onChange={set("phone")} placeholder="Telefon" className={inputCls} data-testid="input-phone" />
              <select value={form.service} onChange={set("service")} className={inputCls} data-testid="input-service">
                <option value="" className="bg-ink">Leistung wählen</option>
                {["Kernbohrungen","Schlitzarbeiten","Dosenbohrungen","Kabelzug","Kabelkanäle","Leerrohre"].map((s) => (
                  <option key={s} value={s} className="bg-ink">{s}</option>
                ))}
              </select>
            </div>
            <textarea required rows={6} value={form.message} onChange={set("message")} placeholder="Ihre Nachricht *" className={`${inputCls} resize-none`} data-testid="input-message" />
            <button type="submit" disabled={loading} data-testid="submit-contact"
              className="mt-2 flex items-center justify-center gap-2.5 rounded-full bg-volt text-black font-bold px-8 py-4 hover:scale-[1.02] transition-transform duration-300 shadow-[0_0_30px_rgba(245,133,26,0.25)] disabled:opacity-60">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="h-4 w-4" strokeWidth={2.5} /> Anfrage senden</>}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
